import { useState, useEffect } from 'react';
import { useAuth } from '@/providers/AuthProvider';
import { apiClient } from '@/services/apiClient';
import type { Employee } from '@/types/timesheet';
import type { WootricUser } from '@/types/wootric';

interface JwtPayload {
  sub?: string;
  email?: string;
  preferred_username?: string;
  name?: string;
  exp?: number;
  iat?: number;
}

function decodeJwt(token: string): JwtPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch {
    return null;
  }
}

interface UseUserProfileResult {
  user: WootricUser | null;
  isLoading: boolean;
  error: Error | null;
}

export function useUserProfile(): UseUserProfileResult {
  const { getAccessToken } = useAuth();
  const [user, setUser] = useState<WootricUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!getAccessToken) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const token = await getAccessToken();

        if (!token) {
          setIsLoading(false);
          return;
        }

        const jwtPayload = decodeJwt(token);
        const email = jwtPayload?.email || jwtPayload?.preferred_username || '';
        const userId = jwtPayload?.sub || '';

        if (!email) {
          setIsLoading(false);
          return;
        }

        let role: string | undefined;
        let startDate: Date | undefined;

        try {
          const employee = await apiClient.get<Employee>('/employees/me');
          role = employee.role;
          startDate = employee.startDate ? new Date(employee.startDate) : undefined;
        } catch {
          console.warn('Failed to fetch employee data, using JWT claims only');
        }

        setUser({
          email,
          userId,
          role,
          createdAt: startDate,
        });
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to get user profile'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserProfile();
  }, [getAccessToken]);

  return { user, isLoading, error };
}
